import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Inspection,
  ChecklistResponseStatus,
  OverallResult,
  FindingSeverity,
  InspectionChecklistTemplate,
} from '../types';
import { inspectionService } from '../inspectionService';

interface InspectionDetailModalProps {
  inspection: Inspection;
  onClose: () => void;
  onRefresh: () => void;
}

export const InspectionDetailModal: React.FC<InspectionDetailModalProps> = ({
  inspection: initialInspection,
  onClose,
  onRefresh,
}) => {
  const [inspection, setInspection] = useState<Inspection>(initialInspection);
  const [activeTab, setActiveTab] = useState<'CHECKLIST' | 'FINDINGS' | 'REPORT' | 'DETAILS'>('CHECKLIST');
  const [loading, setLoading] = useState(false);

  // Containment dialog state
  const [showContainmentModal, setShowContainmentModal] = useState(false);
  const [containmentNotes, setContainmentNotes] = useState('');

  // Complete field check dialog state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState<OverallResult>('SATISFACTORY');

  // New finding state
  const [showAddFindingModal, setShowAddFindingModal] = useState(false);
  const [findingTitle, setFindingTitle] = useState('');
  const [findingSeverity, setFindingSeverity] = useState<FindingSeverity>('LEVEL_2_MAJOR');
  const [findingDescription, setFindingDescription] = useState('');
  const [containmentAction, setContainmentAction] = useState('');
  const [findingTargetDate, setFindingTargetDate] = useState(
    new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]
  );

  // New report state
  const [reportSummary, setReportSummary] = useState('');
  const [leadSign, setLeadSign] = useState(inspection.leadInspectorName);

  // Templates for attaching checklist
  const [templates, setTemplates] = useState<InspectionChecklistTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const isClosed = inspection.status === 'CLOSED';

  // Fetch full details
  const refreshDetails = async () => {
    try {
      setLoading(true);
      const updated = await inspectionService.getInspectionById(inspection.id);
      setInspection(updated);
    } catch (err) {
      console.error('Error fetching inspection details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDetails();
    loadTemplates();
  }, [inspection.id]);

  const loadTemplates = async () => {
    try {
      const data = await inspectionService.getChecklistTemplates(inspection.organizationId);
      setTemplates(data);
      if (data.length > 0) setSelectedTemplateId(data[0].id);
    } catch (err) {
      console.error('Error loading checklist templates:', err);
    }
  };

  const handleStart = async () => {
    try {
      setLoading(true);
      await inspectionService.startInspection(inspection.id);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to start inspection: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyContainment = async () => {
    if (!containmentNotes.trim()) {
      alert('Critical containment explanation is required.');
      return;
    }
    try {
      setLoading(true);
      await inspectionService.applyCriticalContainment(inspection.id, containmentNotes);
      setShowContainmentModal(false);
      setContainmentNotes('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to apply containment: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteFieldCheck = async () => {
    try {
      setLoading(true);
      await inspectionService.completeInspection(inspection.id, selectedResult);
      setShowCompleteModal(false);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to complete field check: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseInspection = async () => {
    if (!confirm('Are you sure you want to close this inspection? Once closed, records become read-only.')) {
      return;
    }
    try {
      setLoading(true);
      await inspectionService.closeInspection(inspection.id);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to close inspection: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAttachChecklist = async () => {
    if (!selectedTemplateId) return;
    try {
      setLoading(true);
      await inspectionService.createChecklistInstance(inspection.id, selectedTemplateId);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to attach checklist: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async (
    instanceId: string,
    questionId: string,
    responseStatus: ChecklistResponseStatus,
    notes?: string
  ) => {
    try {
      await inspectionService.recordResponse(instanceId, {
        questionId,
        responseStatus,
        notes,
      });
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to record response: ' + (err?.message || err));
    }
  };

  const handleCreateFinding = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!findingTitle.trim() || !findingDescription.trim()) {
      alert('Finding title and description are required.');
      return;
    }
    try {
      setLoading(true);
      await inspectionService.createFinding(inspection.id, {
        title: findingTitle,
        severity: findingSeverity,
        description: findingDescription,
        containmentAction: containmentAction.trim() || undefined,
        targetDate: findingTargetDate,
      });
      setShowAddFindingModal(false);
      setFindingTitle('');
      setFindingDescription('');
      setContainmentAction('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to save finding: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    if (!reportSummary.trim()) {
      alert('Report summary is required.');
      return;
    }
    try {
      setLoading(true);
      await inspectionService.createReport(inspection.id, {
        summary: reportSummary,
        leadInspectorSign: leadSign,
      });
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Rapor olusturulamadi: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReport = async () => {
    try {
      setLoading(true);
      await inspectionService.approveReport(inspection.id, 'QUALITY_SUPERVISOR_MERT');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Rapor onaylanamadi: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const checklistInstance = inspection.checklistInstances && inspection.checklistInstances[0];

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header" style={{ background: '#0f172a', color: '#ffffff', padding: '20px 28px' }}>
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, background: '#2563eb', color: '#ffffff', padding: '2px 8px', borderRadius: '4px' }}>
                {inspection.inspectionNumber}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, background: '#334155', color: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}>
                {inspection.inspectionType}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, background: '#1e293b', color: '#ffffff', border: '1px solid #475569', padding: '2px 8px', borderRadius: '4px' }}>
                {inspection.status}
              </span>
            </div>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#ffffff', margin: '0 0 6px 0', lineHeight: 1.3 }}>{inspection.title}</h2>
            <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <span>Departman: <strong style={{ color: '#f8fafc' }}>{inspection.department}</strong></span>
              <span>Konum: <strong style={{ color: '#f8fafc' }}>{inspection.location}</strong></span>
              {inspection.assetReference && (
                <span>Varlik / Tescil: <strong style={{ color: '#38bdf8', fontFamily: 'monospace' }}>✈ {inspection.assetReference}</strong></span>
              )}
              {inspection.flightNumber && (
                <span>Ucus: <strong style={{ color: '#38bdf8', fontFamily: 'monospace' }}>FLT: {inspection.flightNumber}</strong></span>
              )}
              <span>Bas Mufettis: <strong style={{ color: '#f8fafc' }}>{inspection.leadInspectorName}</strong></span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            style={{ color: '#94a3b8' }}
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Critical Containment Alert Banner */}
        {inspection.criticalContainmentApplied && (
          <div className="bg-red-600 text-white px-5 py-3 flex items-start justify-between shadow-inner">
            <div className="flex items-start gap-3">
              <span className="text-xl animate-bounce">🚨</span>
              <div>
                <h4 className="font-extrabold text-sm uppercase tracking-wide">
                  CRITICAL SAFETY STOP-WORK / GROUNDING ACTIVE
                </h4>
                <p className="text-xs text-red-100 mt-0.5">
                  Onlem Notu: {inspection.containmentNotes || 'Acil ucus durdurma veya ekipman muhurleme aksiyonu alindi.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {['DRAFT', 'PLANNED', 'SCHEDULED', 'ASSIGNED'].includes(inspection.status) && (
              <button
                onClick={handleStart}
                disabled={loading}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs transition"
              >
                ▶ Start Inspection
              </button>
            )}

            {inspection.status === 'IN_PROGRESS' && (
              <button
                onClick={() => setShowCompleteModal(true)}
                disabled={loading}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs transition"
              >
                ✓ Complete Field Check
              </button>
            )}

            {inspection.status === 'REPORT_APPROVED' && (
              <button
                onClick={handleCloseInspection}
                disabled={loading}
                className="px-3 py-1.5 bg-slate-800 hover:bg-black text-white text-xs font-bold rounded-lg shadow-xs transition"
              >
                🔒 Close Inspection
              </button>
            )}

            {!inspection.criticalContainmentApplied && !isClosed && (
              <button
                onClick={() => setShowContainmentModal(true)}
                disabled={loading}
                className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 text-xs font-bold rounded-lg transition"
              >
                ⚠ Apply Critical Containment / Stop-Work
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs">
            {inspection.overallResult && (
              <span className="font-semibold text-gray-700">
                Final Result: <span className="font-bold text-blue-700">{inspection.overallResult}</span>
              </span>
            )}
            {isClosed && (
              <span className="px-2 py-0.5 bg-gray-200 text-gray-700 font-bold rounded">
                CLOSED (READ ONLY)
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-5 bg-white space-x-6">
          <button
            onClick={() => setActiveTab('CHECKLIST')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'CHECKLIST'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Checklist Runtime {checklistInstance ? `(${checklistInstance.scorePercentage}%)` : ''}
          </button>
          <button
            onClick={() => setActiveTab('FINDINGS')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'FINDINGS'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Findings ({inspection.findings?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('REPORT')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'REPORT'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Report & Approval {inspection.report ? `(${inspection.report.status})` : ''}
          </button>
          <button
            onClick={() => setActiveTab('DETAILS')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'DETAILS'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Metadata & Record
          </button>
        </div>

        {/* Tab Contents */}
        <div className="p-5 flex-1 overflow-y-auto space-y-4 bg-gray-50/30">
          {/* TAB 1: CHECKLIST */}
          {activeTab === 'CHECKLIST' && (
            <div className="space-y-4">
              {!checklistInstance ? (
                <div className="bg-white p-6 rounded-lg border border-dashed border-gray-300 text-center space-y-3">
                  <p className="text-sm text-gray-600 font-medium">
                    No checklist instance has been attached to this inspection yet.
                  </p>
                  {!isClosed && (
                    <div className="inline-flex items-center gap-2">
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        className="text-xs border border-gray-300 rounded px-3 py-1.5 bg-white"
                      >
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.code} - {t.title} ({t.inspectionType})
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={handleAttachChecklist}
                        disabled={loading || !selectedTemplateId}
                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded"
                      >
                        Attach Checklist
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Score & Progress Summary */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <h3 className="font-bold text-gray-900 text-sm">{checklistInstance.title}</h3>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Total Items: {checklistInstance.totalItems} | Passed: {checklistInstance.passedCount} | Failed: {checklistInstance.failedCount}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-xs font-bold text-gray-500 uppercase">Compliance Score</div>
                        <div className={`text-xl font-extrabold ${checklistInstance.scorePercentage >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                          %{checklistInstance.scorePercentage}
                        </div>
                      </div>
                      <div className="w-24 bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full ${checklistInstance.scorePercentage >= 80 ? 'bg-green-500' : 'bg-amber-500'}`}
                          style={{ width: `${checklistInstance.scorePercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Checklist Questions List */}
                  <div className="space-y-3">
                    {checklistInstance.responses?.map((resp, index) => (
                      <div
                        key={resp.id}
                        className={`bg-white p-4 rounded-lg border shadow-xs transition ${
                          resp.responseStatus === 'FAIL'
                            ? 'border-red-300 bg-red-50/20'
                            : resp.responseStatus === 'PASS'
                            ? 'border-green-200 bg-green-50/10'
                            : 'border-gray-200'
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                Soru #{index + 1}
                              </span>
                              <span
                                className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                  resp.responseStatus === 'PASS'
                                    ? 'bg-green-100 text-green-800'
                                    : resp.responseStatus === 'FAIL'
                                    ? 'bg-red-100 text-red-800'
                                    : resp.responseStatus === 'OBSERVATION'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                {resp.responseStatus}
                              </span>
                            </div>
                            <p className="text-sm font-semibold text-gray-900">{resp.questionText}</p>
                            {resp.notes && (
                              <p className="text-xs text-gray-500 italic mt-1">Not: {resp.notes}</p>
                            )}
                          </div>

                          {/* Response Radio Controls */}
                          {!isClosed && (
                            <div className="flex items-center gap-1.5 shrink-0">
                              {(['PASS', 'FAIL', 'OBSERVATION', 'NOT_APPLICABLE'] as ChecklistResponseStatus[]).map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() => handleRecordResponse(checklistInstance.id, resp.questionId, status, resp.notes)}
                                  className={`px-2.5 py-1 text-xs font-bold rounded border transition ${
                                    resp.responseStatus === status
                                      ? status === 'PASS'
                                        ? 'bg-green-600 text-white border-green-600'
                                        : status === 'FAIL'
                                        ? 'bg-red-600 text-white border-red-600'
                                        : status === 'OBSERVATION'
                                        ? 'bg-yellow-500 text-white border-yellow-500'
                                        : 'bg-gray-600 text-white border-gray-600'
                                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                  }`}
                                >
                                  {status === 'PASS' && '✓ CONFORMING'}
                                  {status === 'FAIL' && '✕ NON-CONFORMING'}
                                  {status === 'OBSERVATION' && '👁 GOZLEM'}
                                  {status === 'NOT_APPLICABLE' && 'N/A'}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: FINDINGS */}
          {activeTab === 'FINDINGS' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">Inspection Findings</h3>
                  <p className="text-xs text-gray-500">
                    Saha teftisinde tespit edilen Level 1, Level 2 veya gozlem kayitlari.
                  </p>
                </div>
                {!isClosed && (
                  <button
                    onClick={() => setShowAddFindingModal(true)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs"
                  >
                    + Add New Finding
                  </button>
                )}
              </div>

              {(!inspection.findings || inspection.findings.length === 0) ? (
                <div className="bg-white p-8 text-center text-gray-400 rounded-lg border border-dashed border-gray-300">
                  No findings logged for this inspection yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {inspection.findings.map((finding) => (
                    <div
                      key={finding.id}
                      className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-2"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                            {finding.findingCode}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                              finding.severity === 'LEVEL_1_CRITICAL'
                                ? 'bg-red-100 text-red-800'
                                : finding.severity === 'LEVEL_2_MAJOR'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {finding.severity}
                          </span>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                            {finding.status}
                          </span>
                        </div>
                        <span className="text-xs font-mono text-gray-500">Target: {finding.targetDate}</span>
                      </div>

                      <h4 className="font-bold text-gray-900 text-sm">{finding.title}</h4>
                      <p className="text-xs text-gray-600">{finding.description}</p>

                      {finding.containmentAction && (
                        <div className="bg-amber-50 p-2.5 rounded border border-amber-200 text-xs text-amber-900">
                          <strong>Immediate Containment Action:</strong> {finding.containmentAction}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: REPORT & APPROVAL */}
          {activeTab === 'REPORT' && (
            <div className="space-y-4">
              {inspection.report ? (
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-xs space-y-4">
                  <div className="flex justify-between items-start border-b border-gray-100 pb-3">
                    <div>
                      <span className="font-mono text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                        {inspection.report.reportNumber}
                      </span>
                      <h3 className="font-bold text-gray-900 text-base mt-1">Field Inspection Report</h3>
                    </div>
                    <span
                      className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                        inspection.report.status === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-indigo-100 text-indigo-800'
                      }`}
                    >
                      {inspection.report.status}
                    </span>
                  </div>

                  <div>
                    <h5 className="text-xs font-bold text-gray-500 uppercase">Executive Summary & Findings</h5>
                    <p className="text-sm text-gray-800 mt-1 whitespace-pre-line bg-gray-50 p-3 rounded border border-gray-200">
                      {inspection.report.summary}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                    <div className="bg-blue-50/50 p-3 rounded border border-blue-100 text-xs">
                      <div className="font-bold text-blue-900">Inspector Digital Signature:</div>
                      <div className="font-medium text-blue-800 mt-1">{inspection.report.leadInspectorSign || 'Signed'}</div>
                    </div>

                    <div className="bg-purple-50/50 p-3 rounded border border-purple-100 text-xs">
                      <div className="font-bold text-purple-900">Quality Supervisor Approval:</div>
                      <div className="font-medium text-purple-800 mt-1">
                        {inspection.report.qualitySupervisorSign ? (
                          <>
                            {inspection.report.qualitySupervisorSign}
                            <span className="block text-[11px] text-gray-500">{inspection.report.approvedAt}</span>
                          </>
                        ) : (
                          <span className="text-amber-600 font-semibold">Pending Approval</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {inspection.report.status === 'DRAFT' && !isClosed && (
                    <div className="pt-3 flex justify-end">
                      <button
                        onClick={handleApproveReport}
                        disabled={loading}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs"
                      >
                        ✓ Approve Report as Quality Supervisor
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-xs space-y-4">
                  <h3 className="font-bold text-gray-900 text-base">Generate Draft Field Report</h3>
                  <p className="text-xs text-gray-500">
                    When the field inspection is concluded, the lead inspector drafts a report summarizing findings and compliance metrics.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Rapor Ozeti & Degerlendirme *</label>
                      <textarea
                        rows={4}
                        value={reportSummary}
                        onChange={(e) => setReportSummary(e.target.value)}
                        placeholder="Orn: B737-800 TC-TUR ucaginda yapilan transit turnaround incelemesinde FOD ve emniyet techizatlari incelendi..."
                        className="w-full text-sm border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Inspector Digital Signature</label>
                      <input
                        type="text"
                        value={leadSign}
                        onChange={(e) => setLeadSign(e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
                      />
                    </div>

                    <div className="pt-2 flex justify-end">
                      <button
                        onClick={handleCreateReport}
                        disabled={loading || !reportSummary.trim()}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs"
                      >
                        Sign Report & Submit for Approval
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: DETAILS */}
          {activeTab === 'DETAILS' && (
            <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-xs space-y-4 text-xs">
              <h3 className="font-bold text-gray-900 text-sm">Audit Metadata & Traceability</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-gray-700">
                <div>
                  <span className="text-gray-400 block">Kayit ID:</span>
                  <span className="font-mono">{inspection.id}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Organizasyon ID:</span>
                  <span className="font-mono">{inspection.organizationId}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Plan ID:</span>
                  <span className="font-mono">{inspection.planId || 'Bagimsiz / Ad-Hoc'}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Olusturan:</span>
                  <span>{inspection.createdBy}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Olusturulma Tarihi:</span>
                  <span>{inspection.createdAt}</span>
                </div>
                <div>
                  <span className="text-gray-400 block">Son Guncelleme:</span>
                  <span>{inspection.updatedAt}</span>
                </div>
              </div>

              {inspection.notes && (
                <div className="pt-3 border-t border-gray-100">
                  <span className="text-gray-400 block font-semibold mb-1">Mufettis Notlari:</span>
                  <p className="text-gray-700 bg-gray-50 p-2.5 rounded border border-gray-200">{inspection.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>

      {/* MODAL: Apply Critical Containment */}
      {showContainmentModal && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }}>
          <div className="modal-card max-w-md">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3 style={{ color: '#b91c1c' }}>🚨 Critical Safety Stop-Work Order</h3>
                <p>Mandatory aircraft grounding (AOG) or operational stop</p>
              </div>
              <button onClick={() => setShowContainmentModal(false)} className="modal-close-btn" title="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <p style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.45, margin: 0 }}>
                This mandatory order grounds aircraft (AOG), pauses flight dispatch, or halts hangar operations immediately for safety reasons.
              </p>
              <div className="form-field">
                <label>Containment Order Justification & Details *</label>
                <textarea
                  rows={3}
                  value={containmentNotes}
                  onChange={(e) => setContainmentNotes(e.target.value)}
                  placeholder="e.g. Structural pylon hairline fracture identified on TC-TUR; aircraft immediately grounded pending OEM inspection..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowContainmentModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyContainment}
                disabled={loading || !containmentNotes.trim()}
                className="btn-primary"
                style={{ backgroundColor: '#dc2626' }}
              >
                Issue Stop-Work Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Complete Field Check */}
      {showCompleteModal && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }}>
          <div className="modal-card max-w-md">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Complete Field Inspection</h3>
                <p>Record overall outcome to proceed to report drafting</p>
              </div>
              <button onClick={() => setShowCompleteModal(false)} className="modal-close-btn" title="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div className="form-field">
                <label>Overall Assessment Outcome *</label>
                <select
                  value={selectedResult}
                  onChange={(e) => setSelectedResult(e.target.value as OverallResult)}
                >
                  <option value="SATISFACTORY">SATISFACTORY - No findings</option>
                  <option value="ACCEPTABLE">ACCEPTABLE - Minor observations</option>
                  <option value="UNSATISFACTORY">UNSATISFACTORY - Corrective actions mandatory</option>
                  <option value="CRITICAL_FAIL">CRITICAL_FAIL - Immediate safety intervention required</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowCompleteModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCompleteFieldCheck}
                disabled={loading}
                className="btn-primary"
              >
                Complete & Proceed to Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Add Finding */}
      {showAddFindingModal && (
        <div className="modal-backdrop" style={{ zIndex: 1100 }}>
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>New Field Inspection Finding</h3>
                <p>Identify observation or non-conformance item</p>
              </div>
              <button onClick={() => setShowAddFindingModal(false)} className="modal-close-btn" title="Kapat">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleCreateFinding} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              <div className="modal-body space-y-4">
                <div className="form-field">
                  <label>Finding Title *</label>
                  <input
                    type="text"
                    required
                    value={findingTitle}
                    onChange={(e) => setFindingTitle(e.target.value)}
                    placeholder="e.g. Missing 'Remove Before Flight' streamer on LH pitot tube cover"
                  />
                </div>
                <div className="form-grid-2">
                  <div className="form-field">
                    <label>Severity Level *</label>
                    <select
                      value={findingSeverity}
                      onChange={(e) => setFindingSeverity(e.target.value as FindingSeverity)}
                    >
                      <option value="LEVEL_1_CRITICAL">LEVEL 1 - Critical (Flight Safety Threat)</option>
                      <option value="LEVEL_2_MAJOR">LEVEL 2 - Major (Standard Non-Compliance)</option>
                      <option value="LEVEL_3_MINOR">LEVEL 3 - Minor</option>
                      <option value="OBSERVATION">OBSERVATION - Procedural Observation</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Target Due Date *</label>
                    <input
                      type="date"
                      required
                      value={findingTargetDate}
                      onChange={(e) => setFindingTargetDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Finding Description *</label>
                  <textarea
                    rows={3}
                    required
                    value={findingDescription}
                    onChange={(e) => setFindingDescription(e.target.value)}
                    placeholder="Observed non-conformance, physical location, and governing standard..."
                  />
                </div>
                <div className="form-field">
                  <label>Immediate Containment Action</label>
                  <input
                    type="text"
                    value={containmentAction}
                    onChange={(e) => setContainmentAction(e.target.value)}
                    placeholder="e.g. New calibrated streamer fitted and inspected immediately."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowAddFindingModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                >
                  Save Finding
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
